import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Bed,
  Bath,
  Ruler,
  ArrowRight,
  PhoneCall,
  Heart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Property } from "@/hooks/useProperties";
import { useFavorites } from "@/hooks/useFavorites";
import PropertyCover from "@/components/property/PropertyCover";
import { useNearViewport } from "@/hooks/useNearViewport";

const globalCache: Record<
  string,
  { images: string[]; detailedTitle: string }
> = {};

interface PropertyCardProps {
  property: Property;
  onPreview: (property: Property) => void;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onPreview,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [broken, setBroken] = useState<string[]>([]);
  const [isHovered, setIsHovered] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { ref: cardRef, isNear } = useNearViewport<HTMLElement>();

  const [extraData, setExtraData] = useState({
    images: property.foto ? [property.foto] : [],
    detailedTitle: property.poblacion || "Property",
    loading: true,
  });

  useEffect(() => {
    if (!isNear) return;
    const fetchDetails = async () => {
      const langMap: Record<string, string> = {
        es: "1",
        en: "2",
        fr: "3",
      };

      const shortLang = (i18n.language || "es").split("-")[0];
      const langId = langMap[shortLang] || "1";

      const cacheKey = `${property.cod_ofer}_${langId}`;

      if (globalCache[cacheKey]) {
        setExtraData({ ...globalCache[cacheKey], loading: false });
        return;
      }

      try {
        const res = await fetch(
          `/api/inmovilla/api_v1.php?action=detail&id=${property.cod_ofer}&lang=${langId}`,
          { credentials: "same-origin" }
        );

        const result = await res.json();

        const ficha = result.ficha || {};
        const desc =
          result.descripciones?.[property.cod_ofer]?.[langId] || {};
        const fotosObj = result.fotos?.[property.cod_ofer] || {};

        const gallery = Object.values(fotosObj).filter(
          (url): url is string =>
            typeof url === "string" && url.includes("http")
        );

        const finalData = {
          images:
            gallery.length > 0
              ? gallery
              : property.foto
              ? [property.foto]
              : [],
          detailedTitle:
            desc.titulo ||
            ficha.titulo ||
            property.poblacion ||
            "Property",
        };

        globalCache[cacheKey] = finalData;
        setExtraData({ ...finalData, loading: false });
      } catch (err) {
        console.error(err);
        setExtraData((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchDetails();
  }, [isNear, property.cod_ofer, i18n.language, property.poblacion, property.foto]);

  useEffect(() => {
    setBroken([]);
    setIndex(0);
  }, [property.cod_ofer]);

  const isRental = Number(property.keyacci) === 2;
  const displayPrice = isRental ? property.precioalq : property.precioinmo;
  const images = extraData.images.filter((src) => src && !broken.includes(src));
  const hasImages = images.length > 0;
  const safeIndex = Math.min(index, Math.max(0, images.length - 1));

  const openProperty = () => navigate(`/properties/${property.cod_ofer}`);

  return (
    <article
      ref={cardRef}
      className="group relative flex h-full cursor-pointer flex-col"
      onClick={openProperty}
      onMouseEnter={() => {
        setIsHovered(true);
        import("@/pages/properties/[id]");
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* IMAGE */}
      <div
        className="relative aspect-[4/3] w-full shrink-0 cursor-zoom-in overflow-hidden bg-[#ece7de]"
        onClick={(e) => {
          e.stopPropagation();
          onPreview(property);
        }}
      >
        <AnimatePresence mode="wait">
          {!isNear ? (
            <div aria-hidden="true" className="absolute inset-0 bg-[#ece7de]" />
          ) : hasImages ? (
            <motion.img
              key={images[safeIndex]}
              src={images[safeIndex]}
              alt={extraData.detailedTitle}
              width={800}
              height={600}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: isHovered ? 1.035 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => {
                const src = images[safeIndex];
                if (!src) return;
                setBroken((current) => (current.includes(src) ? current : [...current, src]));
              }}
            />
          ) : (
            <PropertyCover
              type={property.nbtipo}
              location={property.zona || property.poblacion || property.ciudad}
            />
          )}
        </AnimatePresence>

        {/* SLIDER */}
        {images.length > 1 && isHovered && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-between px-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex(
                  (prev) =>
                    (prev - 1 + images.length) %
                    images.length
                );
              }}
              className="pointer-events-auto flex h-11 w-11 items-center justify-center bg-black/30 text-white backdrop-blur-md transition hover:bg-white hover:text-black"
              aria-label={t("property.actions.previousImage", "Previous image")}
            >
              <ChevronLeft size={22} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIndex(
                  (prev) => (prev + 1) % images.length
                );
              }}
              className="pointer-events-auto flex h-11 w-11 items-center justify-center bg-black/30 text-white backdrop-blur-md transition hover:bg-white hover:text-black"
              aria-label={t("property.actions.nextImage", "Next image")}
            >
              <ChevronRight size={22} />
            </button>
          </div>
        )}

        <div className="absolute top-4 left-4 z-20">
          <span className="bg-[#F8F5EF]/95 backdrop-blur-md text-stone-900 px-3.5 py-2 font-oswald text-[10px] uppercase tracking-[0.2em]">
            {isRental
              ? t("property.operation.rent")
              : t("property.operation.sale")}
          </span>
        </div>

        <div className="absolute top-4 right-4 z-30 flex items-center gap-2.5">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(property);
            }}
            className={`w-11 h-11 flex items-center justify-center transition backdrop-blur-md ${
              isFavorite(property.cod_ofer) 
                ? "bg-[#a26850] text-white" 
                : "bg-[#F8F5EF]/90 text-stone-900 hover:bg-[#a26850] hover:text-white"
            }`}
            aria-label="Toggle Favorite"
          >
            <Heart size={18} fill={isFavorite(property.cod_ofer) ? "currentColor" : "none"} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(property);
            }}
            className="w-11 h-11 bg-[#F8F5EF]/90 backdrop-blur-md flex items-center justify-center text-stone-900 hover:bg-[#a26850] hover:text-white transition"
            aria-label={t("property.actions.viewFullscreen", "View fullscreen")}
          >
            <Maximize2 size={18} />
          </button>
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex flex-col flex-1 pt-6">
        <div className="mb-6">
          <div className="flex justify-between items-start gap-4 mb-4">
            <h3 className="min-w-0 flex-1 font-playfair text-[1.35rem] leading-[1.25] text-stone-900 sm:text-[1.65rem]">
              {extraData.detailedTitle}
            </h3>

            <span className="font-oswald text-[10px] text-stone-400 uppercase tracking-[0.18em] mt-2 shrink-0">
              {t("property.ref")} {property.ref}
            </span>
          </div>

          <p className="font-oswald text-stone-500 text-xs flex items-center gap-2.5 uppercase tracking-[0.2em] truncate">
            <span className="w-2 h-2 bg-[#a26850] shrink-0" />
            {property.ciudad || property.poblacion}
          </p>
        </div>

        <div className="flex items-center gap-6 sm:gap-8 py-4 mt-auto border-y border-stone-200/80">
          <SpecItem
            icon={<Bed size={20} strokeWidth={1.5} />}
            value={property.total_hab}
            label={t("property.specs.beds")}
          />
          <SpecItem
            icon={<Bath size={20} strokeWidth={1.5} />}
            value={property.banyos}
            label={t("property.specs.baths")}
          />
          <SpecItem
            icon={<Ruler size={20} strokeWidth={1.5} />}
            value={property.m_cons}
            label={t("property.specs.size")}
          />
        </div>

        <div className="flex justify-between items-end gap-5 pt-6">
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.22em] text-stone-400 font-oswald">
              {t("property.price.label")}
            </span>

            {Number(displayPrice) === 0 ? (
              <div className="flex items-center gap-2.5 text-[#a26850] font-oswald text-xs uppercase tracking-widest">
                <PhoneCall size={15} />
                {t("property.price.onRequest")}
              </div>
            ) : (
              <p className="text-2xl sm:text-[1.75rem] font-oswald font-normal text-stone-900 leading-none tracking-tight">
                {Number(displayPrice).toLocaleString()} €
                {isRental && (
                  <span className="text-xs ml-1.5 text-stone-400 font-oswald uppercase">
                    / mo.
                  </span>
                )}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openProperty();
            }}
            className="group/btn flex shrink-0 items-center gap-3 font-oswald text-xs uppercase tracking-[0.22em] text-stone-700 transition-colors hover:text-[#a26850]"
          >
            <span className="relative pb-1">
              {t("property.actions.viewDetails")}
            </span>
            <ArrowRight
              size={16}
              className="group-hover/btn:translate-x-1 transition"
            />
          </button>
        </div>
      </div>
    </article>
  );
};

const SpecItem = ({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: any;
  label: string;
}) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <div className="text-[#a26850] shrink-0">{icon}</div>
    <div className="flex flex-col min-w-0 leading-tight">
      <span className="font-oswald text-base text-stone-800">
        {value && value !== "0" ? value : "-"}
      </span>
      <span className="font-oswald text-[9px] uppercase tracking-[0.14em] text-stone-400 truncate mt-0.5">
        {label}
      </span>
    </div>
  </div>
);

export default PropertyCard;
